/**
 * The remote signer's side of the conversation.
 *
 * Everything here goes through the worker. The customer has no Supabase
 * session and must not be given one: row level security grants an
 * authenticated user the whole database, so a token that let a customer
 * authenticate would hand them every deal on the lot.
 */

const WORKER = import.meta.env.VITE_PAYMENTS_WORKER as string | undefined;

export interface RemoteSession {
  sessionId: string;
  dealNo: string;
  buyerName: string;
  coBuyerName: string | null;
  vehicle: string;
  vin: string;
  docCount: number;
  consentGiven: boolean;
  signedRoles: ("buyer" | "co_buyer" | "dealer")[];
  packetUrl: string | null;
}

async function call<T>(path: string, body: unknown): Promise<T> {
  if (!WORKER) throw new Error("Signing is not configured for this site.");
  const res = await fetch(`${WORKER.replace(/\/$/, "")}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error ?? "Something went wrong. Call the dealership.");
  return data as T;
}

export const openSigning = (token: string, code: string) =>
  call<RemoteSession>("/sign/open", { token, code });

/** Hash of the disclosure, so the consent record pins the wording shown. */
async function disclosureHash(): Promise<string> {
  const { ESIGN_DISCLOSURE } = await import("../../forms/signatureCertificate");
  const digest = await crypto.subtle.digest(
    "SHA-256", new TextEncoder().encode(ESIGN_DISCLOSURE));
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0")).join("");
}

export async function submitSignature(
  token: string,
  code: string,
  opts: {
    role: "buyer" | "co_buyer";
    image: string;
    initials: string | null;
    withConsent: boolean;
  },
): Promise<void> {
  await call<{ ok: true }>("/sign/submit", {
    token,
    code,
    role: opts.role,
    image: opts.image,
    initials: opts.initials,
    consent: opts.withConsent ? { sha256: await disclosureHash() } : null,
  });
}

/**
 * A link token.
 *
 * 32 characters from a 32-symbol alphabet is 160 bits — far past guessing,
 * which matters because the link is the outer layer of a two-part secret and
 * travels by email.
 */
export function newToken(): string {
  const alphabet = "ABCDEFGHJKMNPQRSTVWXYZ0123456789";
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join("");
}

/** The six digits read out over the phone. */
export function newCode(): string {
  const bytes = crypto.getRandomValues(new Uint32Array(1));
  return String(bytes[0]! % 1_000_000).padStart(6, "0");
}

export async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0")).join("");
}
